import React, { useCallback, useState } from 'react';

import Drawer from '@mui/material/Drawer';
import { useMediaQuery } from '@mui/system';

import { IoCloseSharp } from 'react-icons/io5';
import { IoIosArrowDown } from 'react-icons/io';

import { useShallow } from 'zustand/react/shallow';
import classNames from 'classnames';

import { useRoomStore } from '@src/stores';

import OTooltip from '@src/shared/OTooltip';

import { isWebView } from '@src/helpers/mediaDevices.helpers';

import { TDrawerOptions } from '@src/types';

import styles from './ODrawer.module.scss';

interface IODrawerProps {
  title?: string;
  icon: React.ReactNode;
  actionButton?: {
    icon: React.ReactNode;
  };
  content: React.ReactNode; // Content is passed dynamically
  isDrawerOpen: boolean;
  isArrowHidden?: boolean;
  isTitleHidden?: boolean;
  toggleDrawer: (open: boolean) => void;
  dropdownContent?: React.ReactNode;
  preComponent?: React.ReactNode;
  selectedKey: TDrawerOptions;
  subtitle?: React.ReactNode;
  customHeader?: React.ReactNode; // Custom header to replace default header (e.g., for calculator)
}

const ODrawer: React.FC<IODrawerProps> = ({
  title,
  icon,
  content,
  actionButton,
  isDrawerOpen,
  isArrowHidden = false,
  isTitleHidden = false,
  toggleDrawer,
  dropdownContent,
  preComponent,
  selectedKey,
  subtitle,
  customHeader,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');

  const { isJoinedRoom } = useRoomStore(
    useShallow((state) => ({
      isJoinedRoom: state.isJoinedRoom,
    }))
  );

  const getCloseDrawerTooltip = useCallback(() => {
    switch (selectedKey) {
      case 'chat':
        return 'Close chat';
      case 'polls':
        return 'Close polls';
      case 'participants-list':
        return 'Close attendees list';
      case 'notes':
        return 'Close notepad';
      case 'calculator':
        return 'Close calculator';
      default:
        return '';
    }
  }, [selectedKey]);

  // Determine the appropriate CSS classes based on props and state
  const drawerClasses = classNames(styles.drawerRoot, {
    [styles.mobile]: isMobile,
    [styles.desktop]: !isMobile,
    [styles.mobileNotJoined]: isMobile && !isJoinedRoom,
    [styles.mobileWebView]: isMobile && isJoinedRoom && isWebView(),
    [styles.mobileJoined]: isMobile && isJoinedRoom && !isWebView(),
    [styles.desktopHeight]: !isMobile,
  });

  return (
    <Drawer
      classes={{
        root: drawerClasses,
        paper: styles.drawerPaper,
      }}
      variant="persistent"
      anchor="right"
      open={isDrawerOpen}
    >
      {/* Drawer Header */}
      {isWebView() && title === 'Virtual Backgrounds' ? null : (
        <>
          {customHeader && selectedKey === 'calculator' && isMobile ? (
            <div className={styles.drawerHeader}>
              {customHeader}
              <div className={styles.drawerActionsContainer}>
                <OTooltip title={getCloseDrawerTooltip()} placement="left">
                  <div className={styles.drawerClose} onClick={() => toggleDrawer(false)}>
                    <IoCloseSharp height={20} width={20} />
                  </div>
                </OTooltip>
              </div>
            </div>
          ) : (
        <>
          <div className={styles.drawerHeader}>
            <div className={styles.drawerBody}>
              <div className={styles.drawerIcon}> {icon} </div>
              {preComponent}
              {!isTitleHidden && <h1 className={styles.drawerTitle}>{title}</h1>}
              {subtitle && <div className={styles.drawerSubtitle}>{subtitle}</div>}
              {!isArrowHidden && (
                <div
                  className={classNames(styles.drawerArrow, {
                    [styles.open]: isDropdownOpen,
                  })}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <IoIosArrowDown />
                </div>
              )}
            </div>
            <div className={styles.drawerActionsContainer}>
              {actionButton && <div className={styles.drawerAction}>{actionButton.icon}</div>}
              <OTooltip title={getCloseDrawerTooltip()} placement="left">
                <div className={styles.drawerClose} onClick={() => toggleDrawer(false)}>
                  <IoCloseSharp height={20} width={20} />
                </div>
              </OTooltip>
            </div>
          </div>
          {/* Dropdown Content (Visible when dropdown is open) */}
          {isDropdownOpen && dropdownContent && <div>{dropdownContent}</div>}
            </>
          )}
        </>
      )}

      {/* Drawer Content */}
      <div className={styles.drawerContentContainer}>{content}</div>
    </Drawer>
  );
};

export default ODrawer;
